import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

type DailyStat = {
  date: string;
  clicks: number;
  unique_users: number;
};

interface ShopClicksResponse {
  range: { from: string; to: string; days: number };
  totals: {
    total_clicks: number;
    unique_users: number;
    unique_sessions: number;
    anonymous_clicks: number;
    user_clicks: number;
  };
  daily: DailyStat[];
  by_shop: { shop_name: string; clicks: number; unique_users: number }[];
  by_game: { game_id: number; game_title: string | null; clicks: number }[];
  top_listings: {
    shop_listing_id: number;
    shop_name: string | null;
    listing_name: string | null;
    game_id: number | null;
    game_title: string | null;
    clicks: number;
  }[];
  recent: RecentClick[];
}

interface RecentClick {
  id: number;
  created_at: string;
  shop_name: string;
  shop_listing_id: number;
  listing_name: string | null;
  game_id: number;
  game_title: string | null;
  user_id: number | null;
  user_email: string | null;
  session_id: string | null;
  referer: string | null;
  user_agent: string | null;
}

function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

function StatCard({
  label,
  value,
  sub,
  color = "gray",
}: {
  label: string;
  value: number | string;
  sub?: string;
  color?: "gray" | "blue" | "green" | "purple";
}) {
  const colors: Record<string, string> = {
    gray: "border-gray-200 bg-white",
    blue: "border-blue-200 bg-blue-50",
    green: "border-green-200 bg-green-50",
    purple: "border-purple-200 bg-purple-50",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-sm text-gray-600">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

function DailyChart({ data }: { data: DailyStat[] }) {
  const max = Math.max(...data.map((d) => d.clicks), 1);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="mb-3 text-sm font-semibold text-gray-700">Paspaudimai per dieną</p>
      <div className="flex h-40 items-end gap-1">
        {data.map((d) => {
          const h = Math.max((d.clicks / max) * 100, d.clicks > 0 ? 2 : 0);
          return (
            <div
              key={d.date}
              className="group relative flex-1"
              title={`${d.date}: ${d.clicks} paspaudimų, ${d.unique_users} unikalių naudotojų`}
            >
              <div
                className="w-full rounded-sm bg-blue-400 transition-colors hover:bg-blue-600"
                style={{ height: `${h}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-gray-400">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function ShopTable({
  rows,
}: {
  rows: ShopClicksResponse["by_shop"];
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="mb-3 text-sm font-semibold text-gray-700">Pagal parduotuvę</p>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-gray-500">
          <tr>
            <th className="py-1">Parduotuvė</th>
            <th className="py-1 text-right">Paspaudimai</th>
            <th className="py-1 text-right">Unik. naudotojai</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.shop_name} className="border-t border-gray-100">
              <td className="py-1 font-medium text-gray-900">{r.shop_name}</td>
              <td className="py-1 text-right">{r.clicks}</td>
              <td className="py-1 text-right text-gray-600">{r.unique_users}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={3} className="py-3 text-center text-gray-400">Nėra duomenų</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function GameTable({
  rows,
}: {
  rows: ShopClicksResponse["by_game"];
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="mb-3 text-sm font-semibold text-gray-700">Top žaidimai</p>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-gray-500">
          <tr>
            <th className="py-1">Žaidimas</th>
            <th className="py-1 text-right">Paspaudimai</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.game_id} className="border-t border-gray-100">
              <td className="py-1">
                <Link
                  to={`/games/${r.game_id}`}
                  className="font-medium text-blue-700 hover:underline"
                >
                  {r.game_title || `#${r.game_id}`}
                </Link>
              </td>
              <td className="py-1 text-right">{r.clicks}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={2} className="py-3 text-center text-gray-400">Nėra duomenų</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TopListings({
  rows,
}: {
  rows: ShopClicksResponse["top_listings"];
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="mb-3 text-sm font-semibold text-gray-700">Top parduotuvių nuorodos</p>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-gray-500">
          <tr>
            <th className="py-1">Parduotuvė</th>
            <th className="py-1">Žaidimas</th>
            <th className="py-1 text-right">Paspaudimai</th>
            <th className="py-1" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.shop_listing_id} className="border-t border-gray-100">
              <td className="py-1 font-medium text-gray-900">{r.shop_name}</td>
              <td className="py-1">
                {r.game_id ? (
                  <Link
                    to={`/games/${r.game_id}`}
                    className="text-blue-700 hover:underline"
                  >
                    {r.game_title || `#${r.game_id}`}
                  </Link>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="py-1 text-right">{r.clicks}</td>
              <td className="py-1 text-right">
                <Link
                  to={`/admin/shop_clicks/${r.shop_listing_id}`}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Istorija →
                </Link>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="py-3 text-center text-gray-400">Nėra duomenų</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RecentClicksTable({ rows }: { rows: RecentClick[] }) {
  const formatDate = (iso: string) => new Date(iso).toLocaleString("lt-LT");
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="mb-3 text-sm font-semibold text-gray-700">Paskutiniai paspaudimai</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500">
            <tr>
              <th className="py-1">Laikas</th>
              <th className="py-1">Naudotojas</th>
              <th className="py-1">Parduotuvė</th>
              <th className="py-1">Žaidimas</th>
              <th className="py-1">Šaltinis</th>
              <th className="py-1">Sesija</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-gray-100">
                <td className="py-1 text-gray-600 whitespace-nowrap">{formatDate(c.created_at)}</td>
                <td className="py-1">
                  {c.user_email ? (
                    <span className="text-blue-700">{c.user_email}</span>
                  ) : (
                    <span className="text-gray-400 italic">anonim.</span>
                  )}
                </td>
                <td className="py-1 font-medium text-gray-900">{c.shop_name}</td>
                <td className="py-1">
                  {c.game_id ? (
                    <Link
                      to={`/games/${c.game_id}`}
                      className="text-blue-700 hover:underline"
                    >
                      {c.game_title || `#${c.game_id}`}
                    </Link>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="py-1 max-w-[160px] truncate text-xs text-gray-500">
                  {c.referer || "—"}
                </td>
                <td className="py-1 text-xs text-gray-400 font-mono">
                  {c.session_id ? c.session_id.slice(0, 8) : "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-3 text-center text-gray-400">Nėra paspaudimų</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminShopClicksPage() {
  const [data, setData] = useState<ShopClicksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/admin/shop_clicks?days=${days}`);
      const json = await res.json();
      setData(json);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link
        to="/admin"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        ← Admin valdymo skydas
      </Link>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Parduotuvių paspaudimai</h1>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="input w-40"
        >
          <option value={1}>Paskutinės 24 val.</option>
          <option value={7}>Paskutinės 7 dienos</option>
          <option value={30}>Paskutinės 30 dienų</option>
          <option value={90}>Paskutinės 90 dienų</option>
          <option value={365}>Paskutiniai metai</option>
        </select>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard
              label="Paspaudimai"
              value={data.totals.total_clicks}
              color="blue"
            />
            <StatCard
              label="Unikalūs naudotojai"
              value={data.totals.unique_users}
              color="green"
            />
            <StatCard
              label="Unikalios sesijos"
              value={data.totals.unique_sessions}
              color="purple"
            />
            <StatCard
              label="Prisijungę"
              value={data.totals.user_clicks}
            />
            <StatCard
              label="Anonimai"
              value={data.totals.anonymous_clicks}
            />
          </div>

          <div className="mt-6">
            <DailyChart data={data.daily} />
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <ShopTable rows={data.by_shop} />
            <GameTable rows={data.by_game} />
          </div>

          <div className="mt-6">
            <TopListings rows={data.top_listings} />
          </div>

          <div className="mt-6">
            <RecentClicksTable rows={data.recent} />
          </div>
        </>
      )}
    </div>
  );
}
